import { useEffect, useState } from 'react';
import { ContentWrapper } from '@/components/ContentWrapper';
import { PageSubHeading } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import { EmployeesFilters } from './EmployeesFilters';
import { EmployeesTable } from './EmployeeTable';
import { UserDetailed, UsersDetailedResponse } from '@/models/payment';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { getFilterToParams } from '@/pages/users/UserActiveFilter';
import { useSearchParams } from 'react-router-dom';
import {
    WithConfirmation,
    WithConfirmationWrappedComponentProps
} from '@/utils/withConfirmation';
import { useEmployeeStatusConfirmation } from '@/pages/users/useEmployeeStatusConfirmation';
import { usePagination } from '@/components/shared/TablePagination';

export const AllEmployeesPage = WithConfirmation(
    ({ showConfirmDialog }: WithConfirmationWrappedComponentProps) => {
        const { t } = useTranslation();

        const [searchParams, setSearchParams] = useSearchParams();

        // Get username and activeType from URL params
        const username = searchParams.get('username') || '';
        const activeType = searchParams.get('activeType') || '';

        const [userDetailResponse, setUserDetailResponse] = useState<
            UsersDetailedResponse<UserDetailed>
        >({
            users: [],
            total: 0,
            filteredTotal: 0
        });

        const { setAlert } = useNotificationsSnackbar();
        const { page, resetPage, rowsPerPage, setRowsPerPage } =
            usePagination();

        // Helper to update URL params
        const updateUrlParam = (key: string, value: string) => {
            setSearchParams(
                (prev) => {
                    if (value) {
                        prev.set(key, value);
                    } else {
                        prev.delete(key);
                    }
                    return prev;
                },
                { replace: true }
            );
        };

        const fetchSearch = async () => {
            if (!username || username == '' || username?.length < 3) {
                setUserDetailResponse({
                    users: [],
                    total: 0,
                    filteredTotal: 0
                });
                return;
            }
            try {
                let activeStatus = getFilterToParams(activeType);
                const results =
                    await PaymentsAPIServiceV2.getInstance().searchByUsername({
                        username,
                        limit: rowsPerPage,
                        offset: page * rowsPerPage,
                        ...activeStatus
                    });
                setUserDetailResponse(results.data);
            } catch (_e) {
                setAlert('Error fetching users', 'error');
            }
        };

        const [checked, setChecked] = useState<string[]>([]);

        useEffect(() => {
            fetchSearch();
        }, [username, activeType, page, rowsPerPage]);

        useEffect(() => {
            if (username) {
                resetPage();
            }
        }, [username]);

        const { handleStatusChangeWithCompanyId } =
            useEmployeeStatusConfirmation(showConfirmDialog, fetchSearch);

        return (
            <ContentWrapper>
                <>
                    <PageSubHeading>
                        {t('allEmployees_subHeading')}
                    </PageSubHeading>
                    <EmployeesFilters
                        username={username}
                        onUsernameChange={(name) => {
                            updateUrlParam('username', name);
                        }}
                        selectedActiveType={activeType}
                        onActiveTypeChange={(value) => {
                            updateUrlParam('activeType', value as string);
                        }}
                        onResetFilters={() => {
                            updateUrlParam('username', '');
                            updateUrlParam('activeType', '');
                        }}
                    />
                    <EmployeesTable
                        usersDetailed={userDetailResponse}
                        checked={checked}
                        setChecked={setChecked}
                        allChecked={false}
                        onRowClick={() => {}}
                        onSendActivationMailClicked={() => {}}
                        rowsPerPage={rowsPerPage}
                        total={userDetailResponse.filteredTotal}
                        handleChangeRowsPerPage={(val) => {
                            setRowsPerPage(val);
                        }}
                        onStatusClick={function (
                            userId: string,
                            companyId: string,
                            isActive: boolean
                        ): void {
                            handleStatusChangeWithCompanyId(
                                userId,
                                companyId,
                                isActive,
                                fetchSearch
                            );
                        }}
                    />
                </>
            </ContentWrapper>
        );
    }
);
